/**
 * PocketBase client and auth.
 *
 * The console talks to PocketBase directly. There is no API layer to maintain,
 * and access is governed by collection rules rather than by our own code:
 * operators can read the pipeline collections and update the three the console
 * acts on. Creating and deleting stays superuser-only.
 */
import PocketBase from "pocketbase";

const OPERATORS = "pipeline_operators";

/** Set at build time on Cloudflare; falls back to the production instance. */
const BASE_URL =
  (import.meta.env.VITE_POCKETBASE_URL as string | undefined) ??
  "https://vendingmaskinbase.nuhthing.com";

export const pb = new PocketBase(BASE_URL);

// Overlapping requests to the same path must not cancel each other; several
// panels load concurrently on the same screen.
pb.autoCancellation(false);

export interface Operator {
  id: string;
  email: string;
  name: string;
}

export function currentOperator(): Operator | null {
  const record = pb.authStore.record;
  if (!record || !pb.authStore.isValid) return null;
  return {
    id: record.id,
    email: String(record.email ?? ""),
    name: String(record.name ?? ""),
  };
}

export async function signIn(email: string, password: string): Promise<void> {
  await pb.collection(OPERATORS).authWithPassword(email, password);
}

export function signOut(): void {
  pb.authStore.clear();
}

export function onAuthChange(listener: () => void): () => void {
  return pb.authStore.onChange(listener);
}

/** Turns PocketBase's error shapes into something worth showing a human. */
export function readableError(error: unknown): string {
  if (!error) return "Something went wrong.";

  const status = (error as { status?: number }).status;
  if (status === 400) return "That email and password did not match.";
  if (status === 403) return "You do not have permission to do that.";
  if (status === 404) return "That record no longer exists.";
  if (status === 0) return "Could not reach the server. Check your connection.";

  const message = (error as { message?: string }).message;
  if (message && message !== "Something went wrong while processing your request.") {
    return message;
  }
  return "Something went wrong. Try again.";
}
